import moment from 'moment';
import { ChatSession } from '../../../layouts/MainLayout/slice/types';
import { TimeRangeKey } from '../slice/constants';

export type DailyPoint = {
  day: string; // YYYY-MM-DD
  label: string; // e.g. "Feb 9"
  value: number;
};

function toMoment(dateLike: string) {
  const m = moment(dateLike);
  return m.isValid() ? m : moment.invalid();
}

export function filterByRange(sessions: ChatSession[], range: TimeRangeKey, now = moment()) {
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 1;
  const start = now.clone().startOf('day').subtract(days - 1, 'days');
  return sessions.filter((s) => {
    const m = toMoment(s.createdAt);
    return m.isValid() && m.isSameOrAfter(start);
  });
}

export function bucketDailyCounts(sessions: ChatSession[], range: TimeRangeKey, now = moment()): DailyPoint[] {
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const start = now.clone().startOf('day').subtract(days - 1, 'days');

  const map = new Map<string, number>();
  sessions.forEach((s) => {
    const m = toMoment(s.createdAt);
    if (!m.isValid() || m.isBefore(start)) return;
    const key = m.format('YYYY-MM-DD');
    map.set(key, (map.get(key) ?? 0) + 1);
  });

  const points: DailyPoint[] = [];
  for (let i = 0; i < days; i++) {
    const d = start.clone().add(i, 'days');
    const day = d.format('YYYY-MM-DD');
    points.push({
      day,
      label: d.format('MMM D'),
      value: map.get(day) ?? 0,
    });
  }
  return points;
}

export function percentDelta(current: number, previous: number) {
  if (previous <= 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

export function splitCurrentPrevious(sessions: ChatSession[], range: TimeRangeKey, now = moment()) {
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const currentStart = now.clone().startOf('day').subtract(days - 1, 'days');
  const previousStart = currentStart.clone().subtract(days, 'days');
  const previousEnd = currentStart.clone().subtract(1, 'day').endOf('day');

  const current = sessions.filter((s) => {
    const m = toMoment(s.createdAt);
    return m.isValid() && m.isSameOrAfter(currentStart);
  });
  const previous = sessions.filter((s) => {
    const m = toMoment(s.createdAt);
    return m.isValid() && m.isSameOrAfter(previousStart) && m.isSameOrBefore(previousEnd);
  });
  return { current, previous };
}

export function sortRecent(sessions: ChatSession[], limit = 8) {
  return [...sessions]
    .filter((s) => toMoment(s.createdAt).isValid())
    .sort((a, b) => toMoment(b.createdAt).valueOf() - toMoment(a.createdAt).valueOf())
    .slice(0, limit);
}

export function getMostActiveDay(points: DailyPoint[]) {
  if (points.length === 0) return null;
  let best = points[0];
  for (const p of points) {
    if (p.value > best.value) best = p;
  }
  return best.value > 0 ? best : null;
}

